// Cart utility for sessionStorage. The cart lasts for this browser tab and clears when the tab closes.
// Identity = variant_id (+ pack_id when present). Catalog SKU = variant_sku / sku.
// size/color are display aliases only (selected_size / selected_color).

const CART_KEY = "swagstore_cart";

const dispatchCartUpdate = () => {
  window.dispatchEvent(new Event("cartUpdated"));
};

/** Cart line identity: same variant in different packs stays distinct by pack_id. */
export function cartItemKey(item) {
  const variantId = item?.variant_id ?? "";
  const packId = item?.pack_id ?? "";
  return `${packId}_${variantId}`;
}

/**
 * Soft-migrate legacy cart rows where `sku` was size.
 * Prefer catalog SKU on `variant_sku` / `sku`; never invent size as catalog SKU.
 */
function migrateCartItem(item) {
  if (!item || typeof item !== "object") return item;

  const size = item.selected_size ?? item.size ?? null;
  const skuEqualsSize =
    item.sku != null && size != null && String(item.sku) === String(size);

  const catalogSku =
    item.variant_sku ||
    item.client_sku ||
    (skuEqualsSize ? null : item.sku) ||
    null;

  return {
    ...item,
    variant_id: item.variant_id ?? item.id ?? null,
    variant_sku: catalogSku,
    sku: catalogSku,
    selected_size: item.selected_size ?? size ?? null,
    selected_color: item.selected_color ?? item.color ?? "",
  };
}

export function getCart() {
  try {
    const cart = sessionStorage.getItem(CART_KEY);
    const parsed = cart ? JSON.parse(cart) : [];
    if (!Array.isArray(parsed)) return [];

    const migrated = parsed.map(migrateCartItem);
    try {
      sessionStorage.setItem(CART_KEY, JSON.stringify(migrated));
    } catch {
      // ignore quota / private mode
    }
    return migrated;
  } catch {
    return [];
  }
}

export function setCart(cart) {
  sessionStorage.setItem(CART_KEY, JSON.stringify(cart));
  dispatchCartUpdate();
}

export function addToCart(item) {
  const cart = getCart();
  const incoming = migrateCartItem(item);
  const key = cartItemKey(incoming);
  const idx = cart.findIndex((i) => cartItemKey(i) === key);

  if (idx > -1) {
    const isUnlimited =
      cart[idx].limited_stock === false || incoming.limited_stock === false;
    if (isUnlimited) {
      cart[idx].selected_quantity =
        cart[idx].selected_quantity + incoming.selected_quantity;
    } else {
      const maxQuantity = cart[idx].max_quantity || incoming.max_quantity || 10;
      const newQuantity =
        cart[idx].selected_quantity + incoming.selected_quantity;
      cart[idx].selected_quantity = Math.min(newQuantity, maxQuantity);
      if (incoming.max_quantity) {
        cart[idx].max_quantity = incoming.max_quantity;
      }
    }
    if (incoming.variant_sku) cart[idx].variant_sku = incoming.variant_sku;
    if (incoming.sku) cart[idx].sku = incoming.sku;
  } else {
    cart.push(incoming);
  }
  setCart(cart);
}

/** Update quantity by variant_id (and pack_id when provided). */
export function updateCartItem(variant_id, quantity, pack_id) {
  const cart = getCart();
  const idx = cart.findIndex((i) => {
    if (String(i.variant_id) !== String(variant_id)) return false;
    if (pack_id !== undefined && pack_id !== null) {
      return String(i.pack_id ?? "") === String(pack_id ?? "");
    }
    return true;
  });

  if (idx > -1) {
    if (cart[idx].limited_stock === false) {
      cart[idx].selected_quantity = Math.max(1, quantity);
    } else {
      const maxQuantity = cart[idx].max_quantity || 10;
      cart[idx].selected_quantity = Math.min(quantity, maxQuantity);
    }
    setCart(cart);
  }
}

/** Remove by variant_id (and pack_id when provided). */
export function removeCartItem(variant_id, pack_id) {
  let cart = getCart();
  cart = cart.filter((i) => {
    if (String(i.variant_id) !== String(variant_id)) return true;
    if (pack_id !== undefined && pack_id !== null) {
      return String(i.pack_id ?? "") !== String(pack_id ?? "");
    }
    return false;
  });
  setCart(cart);
}

export function clearCart() {
  setCart([]);
}
